import { 
  Box, 
  Tabs, 
  TabList, 
  TabPanels, 
  Tab, 
  TabPanel, 
  Heading, 
  VStack,
  Text,
  useColorModeValue,
  Button,
  Flex,
  Link as ChakraLink,
  Icon,
  Divider
} from '@chakra-ui/react';
import { UserManagement } from './UserManagement';
import { AccessLogs } from './AccessLogs';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaHome } from 'react-icons/fa';
import { FileUpload } from './FileUpload';
import { ConfirmedMatchReview } from './ConfirmedMatchReview';
import { Link, Outlet } from 'react-router-dom';
import { FiHome, FiUsers, FiSettings, FiDatabase, FiBarChart2, FiMessageSquare } from 'react-icons/fi';
import { MatchAnalytics } from './MatchAnalytics';
import { Settings } from './Settings';
import GptOssBeta from './GptOssBeta';
import { RTCWikiAIFullPage } from './RTCWikiAIFullPage';

function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  if (!user?.access?.admin) {
    return (
      <Box minH="100vh" bg={bgColor} p={8}>
        <Text>You don't have permission to access this page.</Text>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      <Box maxW="1400px" mx="auto" px={6} py={8}>
        <VStack spacing={8} align="stretch">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Heading size="2xl">Admin Dashboard</Heading>
            <Button
              leftIcon={<FaHome />}
              colorScheme="blue"
              size="lg"
              onClick={() => navigate('/')}
              boxShadow="sm"
            >
              Home
            </Button>
          </Box>
          
          <Box
            bg="white"
            borderRadius="xl"
            boxShadow="xl"
            overflow="hidden"
          >
            <Tabs variant="enclosed" colorScheme="blue">
              <TabList bg="gray.50" px={4} pt={4}>
                <Tab 
                  _selected={{ 
                    bg: 'white', 
                    color: 'blue.600',
                    borderBottom: '3px solid',
                    borderColor: 'blue.600',
                    mb: '-1px'
                  }}
                  fontWeight="semibold"
                >
                  User Management
                </Tab>
                <Tab 
                  _selected={{ 
                    bg: 'white', 
                    color: 'blue.600',
                    borderBottom: '3px solid',
                    borderColor: 'blue.600',
                    mb: '-1px'
                  }}
                  fontWeight="semibold"
                >
                  Access Logs
                </Tab>
                <Tab 
                  _selected={{ 
                    bg: 'white', 
                    color: 'blue.600',
                    borderBottom: '3px solid',
                    borderColor: 'blue.600',
                    mb: '-1px'
                  }}
                  fontWeight="semibold"
                >
                  File Upload
                </Tab>
                <Tab 
                  _selected={{ 
                    bg: 'white', 
                    color: 'blue.600',
                    borderBottom: '3px solid',
                    borderColor: 'blue.600',
                    mb: '-1px'
                  }}
                  fontWeight="semibold"
                >
                  Confirmed Matches
                </Tab>
                <Tab 
                  _selected={{ 
                    bg: 'white', 
                    color: 'blue.600',
                    borderBottom: '3px solid',
                    borderColor: 'blue.600',
                    mb: '-1px'
                  }}
                  fontWeight="semibold"
                >
                  <Flex align="center">
                    <Icon as={FiBarChart2} mr={2} />
                    Analytics
                  </Flex>
                </Tab>
                <Tab 
                  _selected={{ 
                    bg: 'white', 
                    color: 'blue.600',
                    borderBottom: '3px solid',
                    borderColor: 'blue.600',
                    mb: '-1px'
                  }}
                  fontWeight="semibold"
                >
                  <Flex align="center">
                    <Icon as={FiSettings} mr={2} />
                    Settings
                  </Flex>
                </Tab>
                <Tab 
                  _selected={{ 
                    bg: 'white', 
                    color: 'blue.600',
                    borderBottom: '3px solid',
                    borderColor: 'blue.600',
                    mb: '-1px'
                  }}
                  fontWeight="semibold"
                >
                  <Flex align="center">
                    <Icon as={FiMessageSquare} mr={2} />
                    RTC Chatbot (BETA)
                  </Flex>
                </Tab>
                <Tab 
                  _selected={{ 
                    bg: 'white', 
                    color: 'blue.600',
                    borderBottom: '3px solid',
                    borderColor: 'blue.600',
                    mb: '-1px'
                  }}
                  fontWeight="semibold"
                >
                  <Flex align="center">
                    <Icon as={FiMessageSquare} mr={2} />
                    RTC Wiki AI
                  </Flex>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={0}>
                  <UserManagement />
                </TabPanel>
                <TabPanel p={0}>
                  <AccessLogs />
                </TabPanel>
                <TabPanel p={0}>
                  <FileUpload />
                </TabPanel>
                <TabPanel p={0}>
                  <ConfirmedMatchReview />
                </TabPanel>
                <TabPanel>
                  <MatchAnalytics />
                </TabPanel>
                <TabPanel>
                  <Settings />
                </TabPanel>
                <TabPanel>
                  <GptOssBeta />
                </TabPanel>
                <TabPanel>
                  <RTCWikiAIFullPage />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
}

export const AdminLayout: React.FC = () => {
  return (
    <Flex h="100vh">
      {/* Sidebar */}
      <Box w="250px" bg="blue.800" color="white" p={5} boxShadow="lg">
        <VStack align="start" spacing={8}>
          <Heading size="md">InSight Admin</Heading>
          
          <VStack align="start" spacing={4} w="100%">
            <Text fontWeight="bold" fontSize="sm" color="gray.300">NAVIGATION</Text>
            
            <ChakraLink as={Link} to="/admin" w="100%" _hover={{ textDecor: 'none' }}>
              <Flex align="center" p={2} borderRadius="md" _hover={{ bg: 'blue.700' }}>
                <Icon as={FiHome} mr={3} />
                <Text>Dashboard</Text>
              </Flex>
            </ChakraLink>
            
            <ChakraLink as={Link} to="/admin/users" w="100%" _hover={{ textDecor: 'none' }}>
              <Flex align="center" p={2} borderRadius="md" _hover={{ bg: 'blue.700' }}>
                <Icon as={FiUsers} mr={3} />
                <Text>User Management</Text>
              </Flex>
            </ChakraLink>
            
            <ChakraLink as={Link} to="/admin/confirmed-matches" w="100%" _hover={{ textDecor: 'none' }}>
              <Flex align="center" p={2} borderRadius="md" _hover={{ bg: 'blue.700' }}>
                <Icon as={FiDatabase} mr={3} />
                <Text>Confirmed Matches</Text>
              </Flex>
            </ChakraLink>
            
            <ChakraLink as={Link} to="/admin/match-analytics" w="100%" _hover={{ textDecor: 'none' }}>
              <Flex align="center" p={2} borderRadius="md" _hover={{ bg: 'blue.700' }}>
                <Icon as={FiBarChart2} mr={3} />
                <Text>Match Analytics</Text>
              </Flex>
            </ChakraLink>
            
            <Divider borderColor="blue.600" />
            
            <ChakraLink as={Link} to="/admin/settings" w="100%" _hover={{ textDecor: 'none' }}>
              <Flex align="center" p={2} borderRadius="md" _hover={{ bg: 'blue.700' }}>
                <Icon as={FiSettings} mr={3} />
                <Text>Settings</Text>
              </Flex>
            </ChakraLink>
          </VStack>
        </VStack>
      </Box>
      
      {/* Main Content */}
      <Box flex="1" overflow="auto" bg="gray.50">
        <Outlet />
      </Box>
    </Flex>
  );
};

export default AdminDashboard; 